import * as Sentry from '@sentry/browser';

import { redactBreadcrumb, redactEvent } from './redact';

type LogMeta = Record<string, unknown>;

interface Logger {
  debug(meta: LogMeta, msg?: string): void;
  info(meta: LogMeta, msg?: string): void;
  warn(meta: LogMeta, msg?: string): void;
  error(meta: LogMeta, msg?: string): void;
}

// Console log level. Debug is OFF by default (the transport's per-conversation
// catch-up sync — `fetchSince` / `skipWelcome` — emits O(conversations) debug
// lines on every bootstrap/resume and floods the console). Opt back in at
// runtime with `localStorage.setItem('ping.logLevel','debug')` + reload, or at
// build time with `VITE_LOG_LEVEL=debug`. warn/error always show.
const LEVEL_ORDER = { debug: 10, info: 20, warn: 30, error: 40 } as const;
type LogLevel = keyof typeof LEVEL_ORDER;

function resolveMinLevel(): LogLevel {
  try {
    if (typeof localStorage !== 'undefined') {
      const ls = localStorage.getItem('ping.logLevel');
      if (ls && ls in LEVEL_ORDER) return ls as LogLevel;
    }
  } catch {
    /* localStorage unavailable (private mode) */
  }
  const env = import.meta.env.VITE_LOG_LEVEL;
  if (typeof env === 'string' && env in LEVEL_ORDER) return env as LogLevel;
  return 'info';
}

const minLevel = LEVEL_ORDER[resolveMinLevel()];

const consoleLogger: Logger = {
  debug(meta, msg) {
    if (LEVEL_ORDER.debug < minLevel) return;
    console.debug(`[ping] ${msg ?? ''}`, meta);
  },
  info(meta, msg) {
    if (LEVEL_ORDER.info < minLevel) return;
    console.info(`[ping] ${msg ?? ''}`, meta);
  },
  warn(meta, msg) {
    console.warn(`[ping] ${msg ?? ''}`, meta);
  },
  error(meta, msg) {
    console.error(`[ping] ${msg ?? ''}`, meta);
  },
};

function createLogger(): Logger {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || typeof dsn !== 'string') {
    return consoleLogger;
  }

  Sentry.init({
    dsn,
    beforeSend: (event, _hint) => redactEvent(event) as Sentry.ErrorEvent | null,
    beforeBreadcrumb: redactBreadcrumb,
    tracesSampleRate: 0.1,
    environment: import.meta.env.MODE || 'production',
  });

  return {
    debug(meta: LogMeta, msg?: string): void {
      Sentry.addBreadcrumb({
        level: 'debug',
        message: msg || '',
        data: meta,
      });
    },

    info(meta: LogMeta, msg?: string): void {
      Sentry.addBreadcrumb({
        level: 'info',
        message: msg || '',
        data: meta,
      });
    },

    warn(meta: LogMeta, msg?: string): void {
      Sentry.addBreadcrumb({
        level: 'warning',
        message: msg || '',
        data: meta,
      });
      Sentry.captureMessage(msg || 'Warning', 'warning');
    },

    error(meta: LogMeta, msg?: string): void {
      Sentry.captureException(new Error(msg || 'Error'), {
        level: 'error',
        extra: meta,
      });
    },
  };
}

export const log: Logger = createLogger();
